import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2 } from "lucide-react";

const ContactForm = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [num1, setNum1] = useState(0);
  const [num2, setNum2] = useState(0);
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const openedAt = useRef(Date.now());

  useEffect(() => {
    setNum1(Math.floor(Math.random() * 9) + 1);
    setNum2(Math.floor(Math.random() * 9) + 1);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot) return;
    if (Date.now() - openedAt.current < 3000) return;
    if (!name.trim() || !email.trim() || !message.trim()) return;
    if (parseInt(captchaAnswer) !== num1 + num2) {
      setError("Incorrect answer — please try again.");
      setCaptchaAnswer("");
      setNum1(Math.floor(Math.random() * 9) + 1);
      setNum2(Math.floor(Math.random() * 9) + 1);
      return;
    }

    setSubmitting(true);
    setError("");
    const { error: err } = await supabase
      .from("contact_submissions" as any)
      .insert({ name: name.trim(), email: email.trim(), message: message.trim() });

    setSubmitting(false);
    if (err) {
      setError("Couldn't send your message. Please email us at info@homeapproach.com.");
    } else {
      setSuccess(true);
    }
  };

  if (success) {
    return (
      <div className="flex items-center gap-2 text-sm text-primary py-2">
        <CheckCircle2 size={16} />
        <span>Message received — we'll be in touch soon.</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2.5">
      {/* Honeypot — hidden from real users, bots fill it */}
      <input
        type="text"
        name="website"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        tabIndex={-1}
        aria-hidden="true"
        style={{ position: "absolute", opacity: 0, height: 0, width: 0, pointerEvents: "none" }}
      />
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="h-8 text-xs"
            required
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Email</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className="h-8 text-xs"
            required
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Message</Label>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="How can we help?"
          className="text-xs resize-none h-16"
          required
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">What is {num1} + {num2}?</Label>
        <Input
          type="number"
          value={captchaAnswer}
          onChange={(e) => setCaptchaAnswer(e.target.value)}
          placeholder="Answer"
          className="h-8 text-xs w-24"
          required
        />
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <Button type="submit" size="sm" disabled={submitting} className="w-full text-xs h-8">
        {submitting ? "Sending…" : "Send Message"}
      </Button>
    </form>
  );
};

export default ContactForm;
